import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Search, Loader2, Image, Tag, BarChart3, Upload, Download, FileSpreadsheet, CheckCircle, XCircle, Link, Globe, ExternalLink, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorProducts() {
    const { user, token } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    
    // CSV Import states
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);
    
    // URL Import states
    const [urlDialogOpen, setUrlDialogOpen] = useState(false);
    const [productUrl, setProductUrl] = useState('');
    const [extracting, setExtracting] = useState(false);
    const [extractedProduct, setExtractedProduct] = useState(null);

    const [formData, setFormData] = useState({
        // Basic Info (Shopify: title, body_html, product_type)
        name: '',
        description: '',
        category: '',
        tags: '',
        // Pricing (Shopify: price, compare_at_price)
        prix: '',
        prix_achat: '',
        // Inventory (Shopify: SKU, barcode, inventory_quantity)
        sku: '',
        barcode: '',
        stock_quantity: '',
        // Shipping (Shopify: weight)
        weight: '',
        // Media (Shopify: images)
        image_url: '',
        images: []
    });
    
    // Image upload state
    const [uploadingImages, setUploadingImages] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/vendor-products`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProducts(data);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleOpenDialog = (product = null) => {
        if (product) {
            setEditingProduct(product);
            // Filter out empty image strings
            const existingImages = (product.images || []).filter(img => img && img.trim() !== '');
            setFormData({
                name: product.name || '',
                description: product.description || '',
                category: product.category || '',
                tags: product.tags || '',
                prix: product.prix?.toString() || '',
                prix_achat: product.prix_achat?.toString() || '',
                sku: product.sku || '',
                barcode: product.barcode || '',
                stock_quantity: product.stock_quantity?.toString() || '',
                weight: product.weight?.toString() || '',
                image_url: product.image_url || '',
                images: existingImages
            });
            // Set existing images as previews
            setImageFiles([]);
            setImagePreviews(existingImages);
        } else {
            setEditingProduct(null);
            setFormData({
                name: '', description: '', category: '', tags: '',
                prix: '', prix_achat: '', sku: '', barcode: '',
                stock_quantity: '', weight: '', image_url: '', images: []
            });
            setImageFiles([]);
            setImagePreviews([]);
        }
        setActiveTab('basic');
        setDialogOpen(true);
    };

    // Handle image file selection
    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        const currentCount = imagePreviews.length;
        const remainingSlots = 5 - currentCount;
        
        if (files.length > remainingSlots) {
            toast.error(`Vous ne pouvez ajouter que ${remainingSlots} image(s) supplémentaire(s)`);
            return;
        }
        
        // Validate file types
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
        if (invalidFiles.length > 0) {
            toast.error('Types de fichiers non supportés. Utilisez: JPG, PNG, WebP, GIF');
            return;
        }
        
        // Validate file sizes (max 5MB each)
        const oversizedFiles = files.filter(f => f.size > 5 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            toast.error('Certaines images dépassent 5MB');
            return;
        }
        
        // Create previews
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setImageFiles(prev => [...prev, ...files]);
        setImagePreviews(prev => [...prev, ...newPreviews]);
    };

    // Remove an image
    const removeImage = (index) => {
        const preview = imagePreviews[index];
        
        // Check if it's an existing image URL or a blob preview
        if (preview.startsWith('blob:')) {
            // It's a new file, remove from imageFiles too
            const fileIndex = index - (imagePreviews.length - imageFiles.length);
            if (fileIndex >= 0) {
                setImageFiles(prev => prev.filter((_, i) => i !== fileIndex));
            }
            URL.revokeObjectURL(preview);
        } else {
            // It's an existing image, remove from formData.images
            setFormData(prev => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== index)
            }));
        }
        
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Le nom du produit est requis');
            return;
        }
        if (!formData.prix || parseFloat(formData.prix) <= 0) {
            toast.error('Le prix doit être supérieur à 0');
            return;
        }

        setSaving(true);
        setUploadingImages(true);
        try {
            // Upload new images if any
            let uploadedUrls = [];
            if (imageFiles.length > 0) {
                const uploadFormData = new FormData();
                imageFiles.forEach(file => {
                    uploadFormData.append('files', file);
                });
                
                const uploadRes = await fetch(`${API_URL}/api/vendor-products/upload-images`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: uploadFormData
                });
                
                if (uploadRes.ok) {
                    const uploadData = await uploadRes.json();
                    uploadedUrls = uploadData.urls;
                } else {
                    const errorData = await uploadRes.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Erreur lors de l\'upload des images');
                }
            }
            
            setUploadingImages(false);
            
            // Combine existing images with newly uploaded ones
            const existingImages = formData.images || [];
            const allImages = [...existingImages, ...uploadedUrls];
            
            const method = editingProduct ? 'PUT' : 'POST';
            const url = editingProduct 
                ? `${API_URL}/api/vendor-products/${editingProduct.id}`
                : `${API_URL}/api/vendor-products`;

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    description: (formData.description || '').trim(),
                    category: (formData.category || '').trim(),
                    tags: (formData.tags || '').trim(),
                    prix: parseFloat(formData.prix),
                    prix_achat: formData.prix_achat ? parseFloat(formData.prix_achat) : 0,
                    sku: (formData.sku || '').trim(),
                    barcode: (formData.barcode || '').trim(),
                    stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : 0,
                    weight: formData.weight ? parseFloat(formData.weight) : 0,
                    images: allImages,
                    image_url: allImages[0] || (formData.image_url || '').trim()
                })
            });

            if (res.ok) {
                toast.success(editingProduct ? 'Produit modifié' : 'Produit ajouté');
                setDialogOpen(false);
                setImageFiles([]);
                setImagePreviews([]);
                fetchProducts();
            } else {
                const errorData = await res.json().catch(() => ({}));
                toast.error(errorData.detail || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Save product error:', error);
            toast.error(error.message || 'Erreur réseau');
        } finally {
            setSaving(false);
            setUploadingImages(false);
        }
    };

    const handleDelete = async (product) => {
        if (!window.confirm(`Supprimer "${product.name}" ?`)) return;

        setDeleting(product.id);
        try {
            const res = await fetch(`${API_URL}/api/vendor-products/${product.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Produit supprimé');
                fetchProducts();
            } else {
                toast.error('Erreur lors de la suppression');
            }
        } catch (error) {
            toast.error('Erreur réseau');
        } finally {
            setDeleting(null);
        }
    };

    // Handle CSV import
    const handleImportCSV = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            toast.error('Veuillez sélectionner un fichier CSV');
            return;
        }

        setImporting(true);
        setImportResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch(`${API_URL}/api/vendor-products/import-csv`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await res.json();

            if (res.ok) {
                setImportResult({
                    success: true,
                    imported: data.imported,
                    skipped: data.skipped,
                    errors: data.errors || [],
                    message: data.message
                });
                
                if (data.imported > 0) {
                    toast.success(`${data.imported} produit(s) importé(s) avec succès !`);
                    fetchProducts();
                }
            } else {
                setImportResult({
                    success: false,
                    message: data.detail || 'Erreur lors de l\'import'
                });
                toast.error(data.detail || 'Erreur lors de l\'import');
            }
        } catch (error) {
            console.error('Import error:', error);
            setImportResult({
                success: false,
                message: 'Erreur réseau'
            });
            toast.error('Erreur réseau');
        } finally {
            setImporting(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Download CSV template
    const handleDownloadTemplate = () => {
        window.open('/templates/produits_template.csv', '_blank');
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-DZ', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0) + ' DA';
    };

    // Handle URL extraction
    const handleExtractFromUrl = async () => {
        if (!productUrl.trim()) {
            toast.error('Veuillez entrer une URL');
            return;
        }

        setExtracting(true);
        setExtractedProduct(null);

        try {
            const res = await fetch(`${API_URL}/api/vendor-products/extract-from-url`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ url: productUrl })
            });

            const data = await res.json();

            if (res.ok && data.success) {
                setExtractedProduct(data.product);
                toast.success(data.message);
            } else {
                toast.error(data.detail || 'Erreur lors de l\'extraction');
            }
        } catch (error) {
            console.error('Extract error:', error);
            toast.error('Erreur réseau');
        } finally {
            setExtracting(false);
        }
    };

    // Use extracted product data
    const handleUseExtractedProduct = () => {
        if (extractedProduct) {
            setFormData({
                name: extractedProduct.name || '',
                description: extractedProduct.description || '',
                category: extractedProduct.category || '',
                tags: '',
                prix: extractedProduct.prix ? String(extractedProduct.prix) : '',
                prix_achat: '',
                sku: extractedProduct.sku || '',
                barcode: '',
                weight: '',
                stock_quantity: '',
                image_url: extractedProduct.image_url || ''
            });
            setUrlDialogOpen(false);
            setDialogOpen(true);
            setEditingProduct(null);
            setActiveTab('basic');
            toast.success('Données importées - Vérifiez et complétez le formulaire');
        }
    };

    return (
        <Layout>
            <div className="space-y-6 overflow-x-hidden" data-testid="vendor-products-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Mes Produits</h1>
                        <p className="text-muted-foreground mt-1 text-sm md:text-base">
                            Gérez votre catalogue (compatible Shopify)
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button 
                            variant="secondary" 
                            onClick={() => {
                                setUrlDialogOpen(true);
                                setProductUrl('');
                                setExtractedProduct(null);
                            }}
                            className="gap-2"
                            data-testid="import-url-btn"
                        >
                            <Link className="w-4 h-4" />
                            Import URL
                        </Button>
                        <Button 
                            variant="secondary" 
                            onClick={() => setImportDialogOpen(true)}
                            className="gap-2"
                            data-testid="import-csv-btn"
                        >
                            <Upload className="w-4 h-4" />
                            Import CSV
                        </Button>
                        <Button onClick={() => handleOpenDialog()} className="btn-luxury" data-testid="add-product-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un produit
                        </Button>
                    </div>
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher par nom, catégorie ou SKU..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-products"
                    />
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <Card className="card-luxury">
                        <CardContent className="flex flex-col items-center justify-center py-12">
                            <Package className="w-16 h-16 text-muted-foreground mb-4" />
                            <p className="text-lg font-medium text-muted-foreground">
                                {searchTerm ? 'Aucun produit trouvé' : 'Aucun produit ajouté'}
                            </p>
                            {!searchTerm && (
                                <Button onClick={() => handleOpenDialog()} className="mt-4" variant="outline">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Ajouter votre premier produit
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredProducts.map((product) => (
                            <Card key={product.id} className="card-luxury overflow-hidden" data-testid={`product-card-${product.id}`}>
                                {/* Product Image */}
                                {product.image_url ? (
                                    <div className="aspect-square bg-slate-100 relative">
                                        <img 
                                            src={product.image_url} 
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                ) : (
                                    <div className="aspect-square bg-slate-100 flex items-center justify-center">
                                        <Package className="w-12 h-12 text-slate-300" />
                                    </div>
                                )}
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg truncate">{product.name}</CardTitle>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {product.category && (
                                            <CardDescription className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                                                {product.category}
                                            </CardDescription>
                                        )}
                                        {product.sku && (
                                            <CardDescription className="text-xs text-slate-400">
                                                SKU: {product.sku}
                                            </CardDescription>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className="text-2xl font-bold text-primary">
                                            {formatCurrency(product.prix)}
                                        </span>
                                        {product.prix_achat > 0 && (
                                            <span className="text-sm text-slate-400">
                                                Coût: {formatCurrency(product.prix_achat)}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Stock & Sync Status */}
                                    <div className="flex items-center gap-2 mb-3 text-xs">
                                        {product.stock_quantity > 0 && (
                                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                Stock: {product.stock_quantity}
                                            </span>
                                        )}
                                        {product.synced_to_shopify && (
                                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                                ✓ Shopify
                                            </span>
                                        )}
                                    </div>
                                    
                                    {product.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                                            {product.description}
                                        </p>
                                    )}
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1"
                                            onClick={() => handleOpenDialog(product)}
                                            data-testid={`edit-product-${product.id}`}
                                        >
                                            <Edit className="w-4 h-4 mr-1" />
                                            Modifier
                                        </Button>
                                        {/* Delete button removed - vendors cannot delete products */}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Summary */}
                {!loading && filteredProducts.length > 0 && (
                    <div className="text-sm text-muted-foreground text-center">
                        {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} dans votre catalogue
                    </div>
                )}
            </div>

            {/* Add/Edit Dialog with Tabs */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className="grid grid-cols-4 w-full">
                            <TabsTrigger value="basic" className="text-xs">
                                <Package className="w-3 h-3 mr-1" />
                                Base
                            </TabsTrigger>
                            <TabsTrigger value="pricing" className="text-xs">
                                <BarChart3 className="w-3 h-3 mr-1" />
                                Prix
                            </TabsTrigger>
                            <TabsTrigger value="inventory" className="text-xs">
                                <Tag className="w-3 h-3 mr-1" />
                                Stock
                            </TabsTrigger>
                            <TabsTrigger value="media" className="text-xs">
                                <Image className="w-3 h-3 mr-1" />
                                Média
                            </TabsTrigger>
                        </TabsList>

                        {/* Basic Info Tab */}
                        <TabsContent value="basic" className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Nom du produit *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Ex: Parfum Ambre Royal 100ml"
                                    data-testid="product-name-input"
                                />
                                <p className="text-xs text-muted-foreground">Shopify: title</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Description détaillée du produit..."
                                    rows={3}
                                    data-testid="product-description-input"
                                />
                                <p className="text-xs text-muted-foreground">Shopify: body_html</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="category">Catégorie</Label>
                                    <Input
                                        id="category"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        placeholder="Homme, Femme..."
                                        data-testid="product-category-input"
                                    />
                                    <p className="text-xs text-muted-foreground">Shopify: product_type</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tags">Tags</Label>
                                    <Input
                                        id="tags"
                                        value={formData.tags}
                                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                                        placeholder="oriental, boisé..."
                                        data-testid="product-tags-input"
                                    />
                                    <p className="text-xs text-muted-foreground">Séparés par virgule</p>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Pricing Tab */}
                        <TabsContent value="pricing" className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="prix">Prix de vente (DA) *</Label>
                                <Input
                                    id="prix"
                                    type="number"
                                    value={formData.prix}
                                    onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                                    placeholder="2500"
                                    data-testid="product-price-input"
                                />
                                <p className="text-xs text-muted-foreground">Shopify: price</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="prix_achat">Prix d'achat / Coût (DA)</Label>
                                <Input
                                    id="prix_achat"
                                    type="number"
                                    value={formData.prix_achat}
                                    onChange={(e) => setFormData({ ...formData, prix_achat: e.target.value })}
                                    placeholder="1500"
                                />
                                <p className="text-xs text-muted-foreground">Pour calculer la marge</p>
                            </div>
                            {formData.prix && formData.prix_achat && (
                                <div className="p-3 bg-green-50 rounded-lg">
                                    <p className="text-sm text-green-700">
                                        <strong>Marge:</strong> {formatCurrency(parseFloat(formData.prix) - parseFloat(formData.prix_achat))} 
                                        ({Math.round(((parseFloat(formData.prix) - parseFloat(formData.prix_achat)) / parseFloat(formData.prix)) * 100)}%)
                                    </p>
                                </div>
                            )}
                        </TabsContent>

                        {/* Inventory Tab */}
                        <TabsContent value="inventory" className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="sku">SKU (Code article)</Label>
                                <Input
                                    id="sku"
                                    value={formData.sku}
                                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                    placeholder="PAR-AMB-100"
                                    data-testid="product-sku-input"
                                />
                                <p className="text-xs text-muted-foreground">Shopify: sku</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="barcode">Code-barres (EAN/UPC)</Label>
                                <Input
                                    id="barcode"
                                    value={formData.barcode}
                                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                                    placeholder="6291234567890"
                                />
                                <p className="text-xs text-muted-foreground">Shopify: barcode</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="stock_quantity">Quantité en stock</Label>
                                    <Input
                                        id="stock_quantity"
                                        type="number"
                                        value={formData.stock_quantity}
                                        onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                                        placeholder="50"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="weight">Poids (grammes)</Label>
                                    <Input
                                        id="weight"
                                        type="number"
                                        value={formData.weight}
                                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                                        placeholder="200"
                                    />
                                    <p className="text-xs text-muted-foreground">Shopify: weight</p>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Media Tab */}
                        <TabsContent value="media" className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Images du produit (max 5)</Label>
                                <div className="border-2 border-dashed border-border rounded-lg p-4">
                                    {/* Image Previews */}
                                    {imagePreviews.length > 0 && (
                                        <div className="grid grid-cols-5 gap-2 mb-4">
                                            {imagePreviews.map((preview, index) => {
                                                // Handle different image URL formats
                                                let imgSrc = preview;
                                                if (!preview.startsWith('blob:') && !preview.startsWith('http')) {
                                                    // Relative path - prefix with API URL
                                                    imgSrc = `${API_URL}${preview}`;
                                                }
                                                return (
                                                    <div key={index} className="relative group">
                                                        <img
                                                            src={imgSrc}
                                                            alt={`Preview ${index + 1}`}
                                                            className="w-full h-16 object-cover rounded border bg-slate-100"
                                                            onError={(e) => {
                                                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImage(index)}
                                                            className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                        {index === 0 && (
                                                            <span className="absolute bottom-0 left-0 right-0 text-[9px] bg-primary text-white text-center">
                                                                Principal
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    {/* Upload Button */}
                                    {imagePreviews.length < 5 && (
                                        <label className="flex flex-col items-center justify-center cursor-pointer py-4 hover:bg-muted/50 rounded transition-colors">
                                            <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                                            <span className="text-sm text-muted-foreground">
                                                Cliquez pour ajouter des images
                                            </span>
                                            <span className="text-xs text-muted-foreground mt-1">
                                                JPG, PNG, WebP, GIF • Max 5MB par image
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/gif"
                                                multiple
                                                onChange={handleImageSelect}
                                                className="hidden"
                                            />
                                        </label>
                                    )}
                                    
                                    {imagePreviews.length >= 5 && (
                                        <p className="text-sm text-center text-muted-foreground py-2">
                                            Maximum 5 images atteint
                                        </p>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground">Shopify: images[] - La première image sera l'image principale</p>
                            </div>
                        </TabsContent>
                    </Tabs>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave} disabled={saving || uploadingImages} data-testid="save-product-btn">
                            {saving || uploadingImages ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {uploadingImages ? 'Upload en cours...' : 'Enregistrement...'}
                                </>
                            ) : (
                                editingProduct ? 'Modifier' : 'Ajouter'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CSV Import Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-green-600" />
                            Import CSV
                        </DialogTitle>
                        <DialogDescription>
                            Importez plusieurs produits en une seule fois depuis un fichier CSV
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Template download */}
                        <Card className="bg-blue-50 border-blue-200">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Download className="w-5 h-5 text-blue-600 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="font-medium text-blue-900">Télécharger le modèle</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            Utilisez ce fichier comme modèle pour respecter le format des colonnes.
                                        </p>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100"
                                            onClick={handleDownloadTemplate}
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Télécharger modèle CSV
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Columns info */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Colonnes du fichier CSV</Label>
                            <div className="bg-muted/50 rounded-lg p-3 text-sm">
                                <div className="grid grid-cols-2 gap-2">
                                    <div><span className="font-medium text-red-600">name*</span> - Nom du produit</div>
                                    <div><span className="font-medium text-red-600">prix*</span> - Prix de vente</div>
                                    <div><span className="text-muted-foreground">prix_achat</span> - Prix d'achat</div>
                                    <div><span className="text-muted-foreground">sku</span> - Code SKU</div>
                                    <div><span className="text-muted-foreground">barcode</span> - Code-barres</div>
                                    <div><span className="text-muted-foreground">category</span> - Catégorie</div>
                                    <div><span className="text-muted-foreground">description</span> - Description</div>
                                    <div><span className="text-muted-foreground">weight</span> - Poids (g)</div>
                                    <div><span className="text-muted-foreground">stock_quantity</span> - Stock</div>
                                    <div><span className="text-muted-foreground">tags</span> - Tags</div>
                                </div>
                                <p className="text-xs text-red-600 mt-2">* Colonnes obligatoires</p>
                            </div>
                        </div>

                        {/* File upload */}
                        <div className="space-y-2">
                            <Label>Sélectionner le fichier CSV</Label>
                            <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleImportCSV}
                                    className="hidden"
                                    id="csv-upload"
                                />
                                <label htmlFor="csv-upload" className="cursor-pointer">
                                    {importing ? (
                                        <div className="flex flex-col items-center">
                                            <Loader2 className="w-10 h-10 text-primary animate-spin" />
                                            <p className="mt-2 text-sm font-medium">Import en cours...</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <Upload className="w-10 h-10 text-muted-foreground" />
                                            <p className="mt-2 text-sm font-medium">Cliquez pour sélectionner un fichier</p>
                                            <p className="text-xs text-muted-foreground">ou glissez-déposez ici</p>
                                        </div>
                                    )}
                                </label>
                            </div>
                        </div>

                        {/* Import results */}
                        {importResult && (
                            <Card className={importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        {importResult.success ? (
                                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                        ) : (
                                            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                                        )}
                                        <div className="flex-1">
                                            <p className={`font-medium ${importResult.success ? 'text-green-900' : 'text-red-900'}`}>
                                                {importResult.message}
                                            </p>
                                            {importResult.success && (
                                                <div className="flex gap-4 mt-2 text-sm">
                                                    <span className="text-green-700">
                                                        ✓ {importResult.imported} importé(s)
                                                    </span>
                                                    {importResult.skipped > 0 && (
                                                        <span className="text-amber-700">
                                                            ⚠ {importResult.skipped} ignoré(s)
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {importResult.errors && importResult.errors.length > 0 && (
                                                <div className="mt-2 text-sm text-red-700">
                                                    <p className="font-medium">Erreurs :</p>
                                                    <ul className="list-disc list-inside mt-1 space-y-1">
                                                        {importResult.errors.slice(0, 5).map((err, i) => (
                                                            <li key={i}>{err}</li>
                                                        ))}
                                                        {importResult.errors.length > 5 && (
                                                            <li>... et {importResult.errors.length - 5} autres erreurs</li>
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setImportDialogOpen(false);
                            setImportResult(null);
                        }}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* URL Import Dialog */}
            <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Globe className="w-5 h-5 text-blue-600" />
                            Import depuis URL
                        </DialogTitle>
                        <DialogDescription>
                            Collez un lien produit et nous extrairons automatiquement les informations
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* URL Input */}
                        <div className="space-y-2">
                            <Label>Lien du produit</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={productUrl}
                                    onChange={(e) => setProductUrl(e.target.value)}
                                    placeholder="https://exemple.com/produit/parfum-homme"
                                    className="flex-1"
                                />
                                <Button 
                                    onClick={handleExtractFromUrl}
                                    disabled={extracting || !productUrl.trim()}
                                >
                                    {extracting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Extraire'
                                    )}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Fonctionne avec la plupart des sites e-commerce (Amazon, Jumia, AliExpress, etc.)
                            </p>
                        </div>

                        {/* Loading state */}
                        {extracting && (
                            <Card className="bg-blue-50 border-blue-200">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                                    <div>
                                        <p className="font-medium text-blue-900">Extraction en cours...</p>
                                        <p className="text-sm text-blue-700">Récupération des informations du produit</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Extracted product preview */}
                        {extractedProduct && (
                            <Card className="border-green-200 bg-green-50">
                                <CardContent className="p-4 space-y-4">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-600 mt-1 shrink-0" />
                                        <div className="flex-1">
                                            <p className="font-medium text-green-900">Produit trouvé !</p>
                                            <p className="text-sm text-green-700">Vérifiez les informations et cliquez sur "Utiliser" pour les importer</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-4">
                                        {/* Product image */}
                                        {extractedProduct.image_url && (
                                            <div className="shrink-0">
                                                <img 
                                                    src={extractedProduct.image_url} 
                                                    alt={extractedProduct.name}
                                                    className="w-24 h-24 object-cover rounded-lg border"
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            </div>
                                        )}
                                        
                                        {/* Product info */}
                                        <div className="flex-1 space-y-2">
                                            <h4 className="font-semibold text-sm line-clamp-2">{extractedProduct.name || 'Nom non trouvé'}</h4>
                                            
                                            {extractedProduct.prix > 0 && (
                                                <p className="text-lg font-bold text-green-700">
                                                    {extractedProduct.prix.toLocaleString()} DA
                                                </p>
                                            )}
                                            
                                            {extractedProduct.description && (
                                                <p className="text-xs text-muted-foreground line-clamp-2">
                                                    {extractedProduct.description}
                                                </p>
                                            )}
                                            
                                            {extractedProduct.category && (
                                                <p className="text-xs">
                                                    <span className="text-muted-foreground">Catégorie:</span> {extractedProduct.category}
                                                </p>
                                            )}
                                            
                                            <a 
                                                href={extractedProduct.source_url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Voir la source
                                            </a>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setUrlDialogOpen(false)}>
                            Annuler
                        </Button>
                        {extractedProduct && (
                            <Button onClick={handleUseExtractedProduct} className="gap-2">
                                <Plus className="w-4 h-4" />
                                Utiliser ces données
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
